import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { formatDateShortMonth } from "@utils";
import { Checkbox, Badge } from "@components";
import { formatAmount, cn } from "@utils";
import {
  LedgerItemTemplate,
  LedgerCheckboxLabel,
  LedgerConnector,
} from "./LedgerItemTemplate.jsx";
import { useFetch } from "@hooks";
import { getInvoiceDetails } from "@api";
import { useInvoiceDetails, useDisputes } from "@flags";
import { usePostHog } from "posthog-js/react";
import { EllipsisHorizontalIcon, DocumentTextIcon, FlagIcon, TagIcon } from "@heroicons/react/16/solid";
import { ReceiptCutoff } from "react-bootstrap-icons";
import { Transition } from "@headlessui/react";

export const InvoiceItem = ({
  inv,
  required,
  selected,
  toggleInvoice,
  setSelectedInvoiceDetails,
  setSelectedRowLedgerTransactionId,
  customerId,
  setIsInvoiceDetailsModalOpen,
  setDisputeTarget,
}) => {
  const posthog = usePostHog();
  const showInvoiceDetails = useInvoiceDetails();
  const disputesEnabled = useDisputes();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuCoords, setMenuCoords] = useState({ top: 0, right: 0 });
  const menuButtonRef = useRef(null);

  const {
    data: invoiceDetails,
  } = useFetch(
    ({ queryKey: [, number, custId] }) => getInvoiceDetails(number, custId),
    ["invoiceDetails", inv.transactionNumber, customerId],
    {
      enabled: !!inv.transactionNumber && !!customerId && showInvoiceDetails,
      staleTime: 5 * 60 * 1000,
      refetchOnMount: false,
      gcTime: 10 * 60 * 1000,
    }
  );

  const openInvoiceDetailsModal = () => {
    posthog?.capture("invoice_details_opened", {
      invoice_id: inv.ledgerTransactionId,
    });
    setSelectedInvoiceDetails(invoiceDetails?.data
      ? { ...invoiceDetails.data, discountAmount: inv.discountAmount }
      : null);
    setSelectedRowLedgerTransactionId?.(inv.ledgerTransactionId);
    setIsInvoiceDetailsModalOpen(true);
    setMenuOpen(false);
  };

  const handleDisputeInvoice = () => {
    setDisputeTarget({
      ledgerTransactionId: inv.ledgerTransactionId,
      invoiceNumber: inv.transactionNumber,
      lineItems: null,
    });
    setMenuOpen(false);
  };

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    if (!menuOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuCoords({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    }
    setMenuOpen(v => !v);
  };

  const creditNoteChildren = inv.creditNotes?.map((cn, idx) => (
    <LedgerItemTemplate
      key={cn.ledgerTransactionId}
      left={<LedgerConnector isFirst={idx === 0} />}
      title={
        (cn.applcableDisocunt ?? 0) !== 0 ? (
          <span className="flex items-center gap-1.5">
            {cn.transactionNumber}
            <TagIcon className="size-3.5 text-green-700 shrink-0" />
          </span>
        ) : cn.transactionNumber
      }
      amount={formatAmount(cn.remainingAmount)}
      amountClassName="text-green-700"
      containerClassName="pb-0"
      dateText={formatDateShortMonth(cn.dueDate)}
    />
  ));

  if (disputesEnabled) {
    const showMenu = !!setDisputeTarget || !!invoiceDetails;
    return (
      <LedgerItemTemplate
        key={inv.ledgerTransactionId}
        left={
          <LedgerCheckboxLabel>
            <Checkbox
              checked={selected.has(inv.ledgerTransactionId)}
              onChange={() => toggleInvoice(inv.ledgerTransactionId)}
              readOnly={required || inv.isDisputed}
            />
          </LedgerCheckboxLabel>
        }
        title={
          <span className="flex items-center gap-1.5">
            {inv.transactionNumber}
            {inv.discountAmount > 0 && (
              <TagIcon className="size-3.5 text-green-700 shrink-0" />
            )}
            {inv.isDisputed && <Badge color="yellow">Disputed</Badge>}
          </span>
        }
        amount={formatAmount(inv.remainingAmount)}
        dateText={formatDateShortMonth(inv.dueDate)}
        trailing={
          showMenu ? (
            <div className="shrink-0">
              <button
                ref={menuButtonRef}
                type="button"
                onClick={handleMenuToggle}
                className="p-1.5 rounded-lg text-primary-500 hover:text-primary-600 hover:bg-primary-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <EllipsisHorizontalIcon className="size-4" />
              </button>

              {menuOpen && createPortal(
                <>
                  <div className="fixed inset-0 z-40" onMouseDown={() => setMenuOpen(false)} />
                  <div
                    className="fixed z-50 bg-white rounded-xl border border-zinc-200 shadow-xl overflow-hidden py-1 min-w-[168px]"
                    style={{ top: menuCoords.top, right: menuCoords.right }}
                  >
                    {!!invoiceDetails && (
                      <button
                        type="button"
                        onMouseDown={openInvoiceDetailsModal}
                        className="w-full text-left flex items-center gap-2.5 px-3.5 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors"
                      >
                        <DocumentTextIcon className="size-3.5 text-zinc-400 shrink-0" />
                        Invoice details
                      </button>
                    )}
                    {setDisputeTarget && (
                      <button
                        type="button"
                        disabled={inv.isDisputed}
                        onMouseDown={() => { if (!inv.isDisputed) handleDisputeInvoice(); else setMenuOpen(false); }}
                        className={cn(
                          "w-full text-left flex items-center gap-2.5 px-3.5 py-2 text-sm transition-colors",
                          inv.isDisputed
                            ? "text-zinc-300 cursor-not-allowed"
                            : "text-red-600 hover:bg-red-50"
                        )}
                      >
                        <FlagIcon className={cn("size-3.5 shrink-0", inv.isDisputed ? "text-zinc-300" : "text-red-400")} />
                        {inv.isDisputed ? "Already disputed" : "Dispute invoice"}
                      </button>
                    )}
                  </div>
                </>,
                document.body
              )}
            </div>
          ) : null
        }
      >
        {creditNoteChildren}
      </LedgerItemTemplate>
    );
  }

  return (
    <LedgerItemTemplate
      key={inv.ledgerTransactionId}
      left={
        <LedgerCheckboxLabel>
          <Checkbox
            checked={selected.has(inv.ledgerTransactionId)}
            onChange={() => toggleInvoice(inv.ledgerTransactionId)}
            readOnly={required}
          />
        </LedgerCheckboxLabel>
      }
      title={
        inv.discountAmount > 0 ? (
          <span className="flex items-center gap-1.5">
            {inv.transactionNumber}
            <TagIcon className="size-3.5 text-green-700 shrink-0" />
          </span>
        ) : inv.transactionNumber
      }
      amount={formatAmount(inv.remainingAmount)}
      dateText={formatDateShortMonth(inv.dueDate)}
      onClick={!!invoiceDetails ? openInvoiceDetailsModal : null}
      onClickActionLabel={
        <Transition
          show={!!invoiceDetails}
          appear
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <span className="text-primary-600 whitespace-nowrap text-xs/6 flex items-center gap-1">
            Invoice details
            <ReceiptCutoff className="size-3 font-bold mx-0.5" />
          </span>
        </Transition>
      }
    >
      {creditNoteChildren}
    </LedgerItemTemplate>
  );
};
